import { useState, useContext } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import de from "date-fns/locale/de";
import { format } from "date-fns";
import { NameContext } from "../context/NameContext";
import { MonoButton } from "../kits";
import ModalWrapper from "./utils/ModalWrapper";

registerLocale("de", de);





// ICS-Generator für Download-Button
// ICS-Generator: Meeting Invite (inkl. method=REQUEST)
function generateICS({ name, date, time, summary, email }) {
  // date: yyyy-mm-dd, time: hh:mm
  const dt = new Date(`${date}T${time}:00`);
  const pad = n => n.toString().padStart(2, "0");
  const toUTC = d =>
    `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}Z`;

  const dtStart = toUTC(dt);
  const dtEnd = toUTC(new Date(dt.getTime() + 60 * 60 * 1000));
  const uid = `${Math.random().toString(36).slice(2)}@denisbitter.de`;
  const esc = s => (s ? String(s).replace(/(\r\n|\r|\n)/g, " ") : "");

  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Denis Bitter Booking//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:REQUEST", // <-- Das macht es zum Meeting-Invite!
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `SUMMARY:${esc(summary)}`,
    `DTSTART:${dtStart}`,
    `DTEND:${dtEnd}`,
    `DTSTAMP:${dtStart}`,
    `ORGANIZER;CN=Denis Bitter:mailto:saghallo@denisbitter.de`,
    email
      ? `ATTENDEE;CN=${esc(name)};RSVP=TRUE:mailto:${email}`
      : "",
    `DESCRIPTION:Online-Termin mit ${esc(name)} per Teams`,
    "SEQUENCE:0",
    "STATUS:CONFIRMED",
    "TRANSP:OPAQUE",
    "END:VEVENT",
    "END:VCALENDAR"
  ]
    .filter(Boolean)
    .join("\r\n");
}

// Download als .ics
function downloadICS({ name, date, time, summary, email }) {
  const ics = generateICS({ name, date, time, summary, email });
  const blob = new Blob([ics], { type: "text/calendar" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "Termin mit Denis Bitter.ics";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}




function BookingModal({ onClose }) {
  const [step, setStep] = useState("date");
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState("");
  const [formData, setFormData] = useState({ name: "", email: "" });
  const [consent, setConsent] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Kontext für den Namen (optional)
  const { name } = useContext(NameContext);

  const timeSlots = [
    "09:00", "09:30", "10:00", "10:30",
    "11:00", "11:30", "15:00", "16:30",
  ];

  const handleInputChange = e => {
    const { name, value } = e.target;
    setFormData(f => ({ ...f, [name]: value }));
  };

  // API zum Senden
  const handleSubmit = async e => {
    e.preventDefault();
    if (!consent) return;
    setIsLoading(true);
    setError(null);

    const payload = {
      name: formData.name,
      email: formData.email,
      date: selectedDate ? format(selectedDate, "yyyy-MM-dd") : "",
      time: selectedTime,
      summary:
        selectedDate && selectedTime
          ? `${format(selectedDate, "dd.MM.yyyy", { locale: de })} um ${selectedTime} per Teams`
          : "",
    };

    try {
      const response = await fetch("https://www.denisbitter.de/stage01/api/booking_mail.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result = await response.json();
      if (result.success) {
        setSuccess(true);
      } else {
        setError(result.message || "Versand nicht erfolgreich.");
      }
    } catch (err) {
      setError("Es gab einen technischen Fehler beim Senden.");
      console.error(err);
    }
    setIsLoading(false);
  };

  // Formatierte Zusammenfassung
  const bookingSummary =
    selectedDate && selectedTime
      ? `${format(selectedDate, "dd.MM.yyyy", { locale: de })} um ${selectedTime} per Teams`
      : "";

  let modalContent;
  if (success) {
    modalContent = (
      <div className="text-center space-y-3">
        <h3 className="text-xs text-black">
          Danke, <br />
          Ich freue mich auf den Termin am <br />
          <div className="mt-3"></div>
          <p className="text-brand bg-gray-900 rounded-md pt-3 pb-3 ">
            {bookingSummary}
          </p>
        </h3>
        <p className="text-xs text-gray-800 font-mono">
          Eine E-Mail ist unterwegs zu Dir.
        </p>
              <MonoButton
              onClick={() =>
                downloadICS({
                  name: formData.name,
                  email: formData.email,
                  date: selectedDate ? format(selectedDate, "yyyy-MM-dd") : "",
                  time: selectedTime,
                  summary: bookingSummary
                })
              }
              className="w-full text-xs mt-2"
            >
              Termin im Kalender speichern
            </MonoButton>
        <MonoButton onClick={onClose} className="mt-4 w-full text-xs">
          Schließen
        </MonoButton>
      </div>
    );
  } else if (step === "date") {
    modalContent = (
      <>
        <h3 className="text-xs text-center text-black font-semibold mb-3">
          Moin {name}, <br />
          <p>Lern mich per Team Call kennen.</p>
          <p className="pt-10">An welchen Tag hättest du Zeit?</p>
        </h3>
        <div className="flex justify-center w-full">
          <DatePicker
            selected={selectedDate}
            onChange={date => setSelectedDate(date)}
            minDate={new Date()}
            locale="de"
            inline
          />
        </div>
        <MonoButton
          className="mt-4 bg-black text-white px-4 py-2 rounded w-full text-center text-xs"
          onClick={() => selectedDate && setStep("time")}
        >
          Weiter
        </MonoButton>
        <MonoButton
          className="mt-2 text-xs text-gray-500 w-full"
          onClick={onClose}
        >
          Abbrechen
        </MonoButton>
      </>
    );
  } else if (step === "time") {
    modalContent = (
      <>
        <h3 className="text-xs text-center text-black mb-3">
          Zu welcher Uhrzeit? {name}
        </h3>
        <div className="grid grid-cols-2 gap-2 text-gray-400">
          {timeSlots.map(slot => (
            <MonoButton
              key={slot}
              className={`px-2 py-2 border rounded text-xs ${
                selectedTime === slot ? "bg-brand text-gray-900" : ""
              }`}
              onClick={() => setSelectedTime(slot)}
            >
              {slot}
            </MonoButton>
          ))}
        </div>
        {selectedTime && selectedDate && (
          <div className="text-xs text-center text-black font-mono mt-10 mb-10">
            Du hast gewählt:
            <p className="text-semibold text-brand bg-gray-950 rounded-md pt-3 pb-3">
              {bookingSummary}
            </p>
          </div>
        )}
        <MonoButton
          className="mt-4 bg-black text-white px-4 py-2 rounded w-full text-xs"
          onClick={() => selectedTime && setStep("form")}
        >
          Weiter
        </MonoButton>
        <MonoButton
          className="mt-2 text-xs text-gray-500 w-full"
          onClick={() => setStep("date")}
        >
          Zurück
        </MonoButton>
      </>
    );
  } else if (step === "form") {
    modalContent = (
      <form className="space-y-3" onSubmit={handleSubmit}>
        <p className="text-center text-xs text-black font-mono mb-10 mt-10">
          Wir lernen uns kennen am:
          <p className="text-brand bg-gray-900 rounded-md pt-3 pb-3 mt-10">
            {bookingSummary}
          </p>
        </p>
        <h3 className="text-xs text-center text-black font-mono mb-3">
          Deine Kontaktdaten {name}
        </h3>
        <input
          type="text"
          name="name"
          placeholder={name ? name : "Name"}
          required
          className="border text-xs rounded px-3 py-2 w-full text-center text-black"
          value={formData.name}
          onChange={handleInputChange}
        />
        <input
          type="email"
          name="email"
          placeholder={name ? `Deine Email ${name} ?` : "E-Mail"}
          required
          className="border text-xs rounded px-3 py-2 w-full text-center text-black"
          value={formData.email}
          onChange={handleInputChange}
        />
        <div className="flex gap-2 text-xs text-black">
          <input
            type="checkbox"
            checked={consent}
            onChange={e => setConsent(e.target.checked)}
            required
          />
          <label className="text-xs text-gray-700 px-2 ">
            Ich stimme der Datenverarbeitung gemäß{" "}
            <a
              href="/datenschutz"
              className="underline hover:text-brand hover:bg-gray-950 hover:pl-1 hover:pr-1"
            >
              Datenschutz
            </a>{" "}
            zu.
          </label>
        </div>
        {error && (
          <div className="text-xs text-center text-red-700 bg-red-100 border border-red-200 rounded p-2">
            {error}
          </div>
        )}
        <button
          type="submit"
          disabled={isLoading || !consent}
          className={`bg-black text-white px-4 py-2 rounded w-full text-xs ${
            (!consent || isLoading) ? "opacity-10 pointer-events-none" : ""
          }`}
        >
          {isLoading ? "Sende..." : "Buchung absenden"}
        </button>
        <button
          type="button"
          className="mt-2 text-xs text-gray-500 w-full "
          onClick={() => setStep("time")}
        >
          Zurück
        </button>
      </form>
    );
  }

  return (
    <ModalWrapper onClose={onClose}>
      {modalContent}
    </ModalWrapper>
  );
}

export default BookingModal;
